import { useEffect } from 'react'
import { Outlet, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, RefreshCw } from 'lucide-react'

import apiClient from '@/lib/api-client'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useOrgStore } from '@/lib/store'
import NotFoundPage from '@/pages/NotFound'
import { MpAlertBanner, MpButton } from '@/ui/components'

interface Organization {
  id: string
  name: string
  role: 'OWNER' | 'ADMIN' | 'VIEWER'
}

interface OrganizationLoadErrorProps {
  isRetrying: boolean
  onRetry: () => void
}

function OrganizationLoadError({ isRetrying, onRetry }: OrganizationLoadErrorProps) {
  return (
    <div className="grid min-h-[calc(100vh-8rem)] place-items-center py-8">
      <section className="w-full max-w-3xl space-y-6">
        <MpAlertBanner tone="warning" icon={AlertTriangle} title="Помилка завантаження">
          Dashboard не зміг перевірити, до яких організацій у вас є доступ.
        </MpAlertBanner>

        <div className="space-y-3">
          <h1 className="text-3xl font-semibold tracking-tight text-[var(--mph-text-primary)] sm:text-4xl">
            Не вдалося завантажити організації
          </h1>
          <p className="max-w-2xl text-sm leading-6 text-[var(--mph-text-secondary)]">
            Перевірте з'єднання або спробуйте ще раз.
          </p>
        </div>

        <MpButton onClick={onRetry} disabled={isRetrying}>
          <RefreshCw className={isRetrying ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          {isRetrying ? 'Повторюємо...' : 'Спробувати ще раз'}
        </MpButton>
      </section>
    </div>
  )
}

/**
 * OrgSync Component
 * 
 * Synchronizes the organization ID from URL parameters (/o/:orgId)
 * to the Zustand store. This ensures the API client always knows
 * the current organization context.
 * 
 * Renders children via <Outlet /> to support nested routes.
 */
export function OrgSync() {
  const { orgId } = useParams<{ orgId: string }>()
  const activeOrgId = useOrgStore((state) => state.activeOrgId)
  const activeOrgRole = useOrgStore((state) => state.activeOrgRole)
  const setActiveOrgId = useOrgStore((state) => state.setActiveOrgId)
  const setActiveOrgRole = useOrgStore((state) => state.setActiveOrgRole)
  const organizationsQuery = useQuery<Organization[]>({
    queryKey: ['organizations', 'list'],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/organizations/')
      return response.data
    },
    enabled: Boolean(orgId),
  })

  const organizations = organizationsQuery.data || []
  const activeOrganization = orgId ? organizations.find((organization) => organization.id === orgId) || null : null
  const isValidOrganization = Boolean(activeOrganization)
  const fallbackOrganizationId = organizations[0]?.id || null

  useEffect(() => {
    if (organizationsQuery.isLoading) {
      return
    }

    if (organizationsQuery.isError) {
      setActiveOrgId(null)
      setActiveOrgRole(null)
      return
    }

    if (activeOrganization && orgId) {
      setActiveOrgId(orgId)
      setActiveOrgRole(activeOrganization.role)
      localStorage.setItem('lastActiveOrgId', orgId)
    } else {
      setActiveOrgId(null)
      setActiveOrgRole(null)
    }
  }, [activeOrganization, orgId, organizationsQuery.isError, organizationsQuery.isLoading, setActiveOrgId, setActiveOrgRole])

  if (organizationsQuery.isLoading) {
    return null
  }

  if (organizationsQuery.isError) {
    return (
      <DashboardLayout navigationOrgId={fallbackOrganizationId}>
        <OrganizationLoadError
          isRetrying={organizationsQuery.isFetching}
          onRetry={() => {
            void organizationsQuery.refetch()
          }}
        />
      </DashboardLayout>
    )
  }

  if (!isValidOrganization) {
    return (
      <DashboardLayout navigationOrgId={fallbackOrganizationId}>
        <NotFoundPage recoveryOrgId={fallbackOrganizationId} />
      </DashboardLayout>
    )
  }

  if (activeOrganization && (activeOrgId !== orgId || activeOrgRole !== activeOrganization.role)) {
    return null
  }

  return <Outlet />
}
